"use client"

import * as React from "react"
import { AppIcon } from "@/components/desk/AppIcon"
import { OpenAppTab } from "@/types/workspace"
import { ArrowLeft, Home, LayoutGrid } from "lucide-react"

interface MobileTaskBarProps {
  activeAppId: string | null
  setActiveAppId: (id: string | null) => void
  openApps: OpenAppTab[]
  showMobileAppSwitcher: boolean
  setShowMobileAppSwitcher: (show: boolean) => void
  setShowMobileControlCenter: (show: boolean) => void
  handleMobileBack: () => void
}

export function MobileTaskBar({
  activeAppId,
  setActiveAppId,
  openApps,
  showMobileAppSwitcher,
  setShowMobileAppSwitcher,
  setShowMobileControlCenter,
  handleMobileBack
}: MobileTaskBarProps) {
  if (activeAppId === null) return null

  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 h-14 bg-zinc-950/95 dark:bg-black/95 backdrop-blur-md border-t border-white/10 z-50 flex items-center justify-between px-3 pb-1 select-none text-white font-sans animate-in slide-in-from-bottom duration-250">
      {/* Left: DeX Control buttons (Voltar, Início, Alternar only) */}
      <div className="flex items-center gap-1 bg-white/5 rounded-lg p-0.5 border border-white/5 shrink-0">
        {/* Voltar */}
        <button 
          onClick={handleMobileBack} 
          className="size-8.5 rounded flex items-center justify-center hover:bg-white/10 active:scale-90 transition-all cursor-pointer text-zinc-300 hover:text-white"
          title="Voltar"
        >
          <ArrowLeft className="size-4.5 text-white" />
        </button>

        {/* Início */}
        <button 
          onClick={() => {
            setActiveAppId(null)
            setShowMobileAppSwitcher(false)
            setShowMobileControlCenter(false)
          }} 
          className="size-8.5 rounded flex items-center justify-center hover:bg-white/10 active:scale-90 transition-all cursor-pointer text-zinc-300 hover:text-white"
          title="Início"
        >
          <Home className="size-4.5 text-white" />
        </button>

        {/* Alternar */}
        <button 
          onClick={() => {
            setShowMobileAppSwitcher(!showMobileAppSwitcher)
            setShowMobileControlCenter(false)
          }} 
          className={`size-8.5 rounded flex items-center justify-center hover:bg-white/10 active:scale-90 transition-all cursor-pointer ${showMobileAppSwitcher ? "bg-[#004EB1] text-white" : "text-zinc-300 hover:text-white"}`}
          title="Alternar"
        >
          <LayoutGrid className="size-4.5 text-white" />
        </button>
      </div>

      {/* Right: Open apps list */}
      <div className="flex items-center justify-end gap-1.5 px-2 overflow-x-auto scrollbar-none flex-1 max-w-[70%]">
        {openApps.map((tab) => {
          const isActive = activeAppId === tab.instanceId
          return (
            <button
              key={tab.instanceId}
              onClick={() => {
                setActiveAppId(tab.instanceId)
                setShowMobileAppSwitcher(false)
              }}
              className={`relative size-8.5 rounded flex items-center justify-center transition-all cursor-pointer shrink-0 ${
                isActive ? "bg-white/15 border border-[#004EB1] scale-105" : "bg-white/5"
              }`}
            >
              <AppIcon iconUrl={tab.app.iconUrl} iframeUrl={tab.app.iframeUrl} name={tab.app.name} size="small" />
              {isActive && <span className="absolute bottom-0.5 w-1 h-1 rounded-full bg-blue-500" />}
            </button>
          )
        })}
      </div>
    </div>
  )
}
